import { useState, useEffect, type ReactNode } from 'react';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { MetricKey } from '@/lib/analytics/metrics';

interface AnalyticsChartCardProps {
  cardKey: MetricKey;
  workspaceId: string;
  title: string;
  isLoading: boolean;
  error: Error | null;
  onRetry?: () => void;
  children: ReactNode;
  filterChips?: ReactNode;
  fullWidth?: boolean;
  totalCurrent?: number;
  totalPrevious?: number | null;
}

const STORAGE_KEY = 'analytics:cardCollapse';

function getCollapseState(workspaceId: string): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY}:${workspaceId}`);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function setCollapseState(workspaceId: string, key: MetricKey, collapsed: boolean): void {
  try {
    const state = getCollapseState(workspaceId);
    state[key] = collapsed;
    localStorage.setItem(`${STORAGE_KEY}:${workspaceId}`, JSON.stringify(state));
  } catch {
    // localStorage may be unavailable (private browsing, quota); silently degrade
  }
}

export function AnalyticsChartCard(props: AnalyticsChartCardProps) {
  const [collapsed, setCollapsed] = useState<boolean>(
    () => getCollapseState(props.workspaceId)[props.cardKey] ?? false,
  );
  useEffect(() => {
    setCollapseState(props.workspaceId, props.cardKey, collapsed);
  }, [collapsed, props.cardKey, props.workspaceId]);

  // Only short-circuit to the empty-state placeholder when the caller has
  // explicitly opted in by passing totalCurrent. Cards that own a more nuanced
  // empty state (e.g. CallOutcomesRibbon shows "No calls in this range")
  // can omit the prop entirely and render their children instead.
  const isEmpty =
    !props.isLoading &&
    !props.error &&
    props.totalCurrent !== undefined &&
    props.totalCurrent === 0 &&
    (props.totalPrevious ?? 0) === 0;

  return (
    <Card className={cn('p-4', props.fullWidth && 'lg:col-span-2')}>
      <div className="flex items-center justify-between mb-2">
        <button
          className="flex items-center gap-2 text-sm font-medium hover:opacity-80"
          onClick={() => setCollapsed((c) => !c)}
          type="button"
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
          {props.title}
        </button>
        {props.filterChips && (
          <div className="flex items-center gap-2">{props.filterChips}</div>
        )}
      </div>
      {!collapsed && (
        <>
          {props.isLoading && <Skeleton className="h-64 w-full" />}
          {props.error && (
            <div className="flex flex-col items-center justify-center h-64 gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              <p className="text-sm text-muted-foreground">Couldn't load</p>
              {props.onRetry && (
                <Button variant="outline" size="sm" onClick={props.onRetry}>
                  Retry
                </Button>
              )}
            </div>
          )}
          {isEmpty && (
            <div className="flex items-center justify-center h-64 text-muted-foreground">
              Not enough data yet
            </div>
          )}
          {!props.isLoading && !props.error && !isEmpty && props.children}
        </>
      )}
    </Card>
  );
}
